import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CropRotate as CropIcon
} from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';
import { logger } from '../../utils/logger';

interface ImageCropDialogProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number;
  outputSize?: { width: number; height: number };
}

/**
 * Helper function to create image from URL
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(new Error(`Failed to load image: ${error}`)));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Helper function to get cropped image
 */
const DEFAULT_OUTPUT_SIZE = { width: 512, height: 512 };

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  outputSize: { width: number; height: number } = DEFAULT_OUTPUT_SIZE
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to desired output size
  canvas.width = outputSize.width;
  canvas.height = outputSize.height;

  // Draw image scaled and cropped to canvas
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize.width,
    outputSize.height
  );

  // Return as blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Canvas is empty'));
      }
    }, 'image/jpeg', 0.95);
  });
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  imageUrl,
  onClose,
  onCropComplete,
  aspectRatio = 1, // Default to square (1:1)
  outputSize = { width: 512, height: 512 }
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropAreaChange = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    if (!croppedAreaPixels) {
      setError('Molimo izaberite deo slike za crop');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels, outputSize);

      // Validate file size (max 5MB)
      if (croppedBlob.size > 5 * 1024 * 1024) {
        setError('Slika je prevelika (max 5MB). Pokušajte sa manjim zoom-om.');
        setProcessing(false);
        return;
      }

      onCropComplete(croppedBlob);
      onClose();
    } catch (err) {
      logger.error('Error cropping image:', err);
      setError('Greška pri obradi slike. Pokušajte ponovo.');
    } finally {
      setProcessing(false);
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 1));
  };

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CropIcon color="primary" />
            <Typography variant="h6">Prilagodi sliku</Typography>
          </Box>
          <IconButton onClick={onClose} disabled={processing} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Alert severity="info" icon={<CropIcon />}>
            <Typography variant="body2">
              Pomerajte i zumrajte sliku da odaberete deo koji želite.
              Rezultujuća slika će biti <strong>{outputSize.width}x{outputSize.height}px</strong>.
            </Typography>
          </Alert>

          {/* Crop Area */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 400,
              backgroundColor: '#000',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropAreaChange}
              showGrid={true}
              style={{
                containerStyle: {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>

          {/* Zoom Controls */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Zoom
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {Math.round(zoom * 100)}%
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 1}>
                <ZoomOutIcon />
              </IconButton>

              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, value) => setZoom(value as number)}
                sx={{ flex: 1 }}
              />

              <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 3}>
                <ZoomInIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              💡 <strong>Saveti:</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              • Povucite sliku da je pomerite<br />
              • Koristite scroll ili zoom kontrole za uvećanje<br />
              • Izaberite najbitniji deo slike
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={processing}
        >
          Otkaži
        </Button>
        <Button
          onClick={handleCropSave}
          variant="contained"
          disabled={processing || !croppedAreaPixels}
        >
          {processing ? 'Obrada...' : 'Sačuvaj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
